import React from 'react';
import { strings } from '../../resources/strings';
import { DataSource } from '../../enums/DataSource';
import { PrescriptionOption } from '../../enums/PrescriptionOption';
import { StatePrescriptionIdentification } from '../../enums/StatePrescriptionIdentification';
import { AssemblyDisapprovalType } from '../../enums/AssemblyDisapprovalType';
import ServicesContext from '../../contexts/ServicesContext';
import { useOrderUtils } from '../../hooks/useOrderUtils';
import { DropdownButton, DropdownButtonColor } from '../../common-components/DropdownButton/DropdownButton';
import { ModalNotificationsV2 } from '../ModalNotificationsV2/ModalNotificationsV2';
import { ModalRecipes } from '../ModalRecipes/ModalRecipes';
import { ModalRecipesV2 } from '../ModalRecipesV2/ModalRecipesV2';
import { ModalDisapproveDegree } from '../ModalDisapproveDegree/ModalDisapproveDegree';
import { ModalRecipesForPhysicalStore } from '../ModalRecipesForPhysicalStore/ModalRecipesForPhysicalStore';

interface IPrescriptionOrderOptionsButtonProps {
  orderId?: number;
  orderNumber?: string;
  orderDataSource?: DataSource,
  orderIsPhysicalStore?: boolean,
  customerEmail?: string;
  customerPhone?: string;
  customerShopifyId?: number;
  customerId?: number;
  currentStateId?: number;
  modalOpened?: () => void;
  modalClosed?: (changed?: boolean) => void;
  statePrescriptionChanged?: (newStateId?: number) => void;
  orderTags?: string;
  orderIsCrossChannelSale?: boolean;
  orderLocationId?: number;
  orderOriginLocationId?: number;
}

export const PrescriptionOrderOptionsButton: React.FC<IPrescriptionOrderOptionsButtonProps> = ({
  orderId,
  orderNumber,
  orderDataSource,
  orderIsPhysicalStore,
  customerEmail,
  customerPhone,
  customerShopifyId,
  customerId,
  currentStateId,
  modalOpened,
  modalClosed,
  statePrescriptionChanged,
  orderTags,
  orderIsCrossChannelSale,
  orderLocationId,
  orderOriginLocationId
}) => {

  const [modalRecipesVisible, setModalRecipesVisible] = React.useState<boolean>(false);
  const [modalRecipesV2Visible, setModalRecipesV2Visible] = React.useState<boolean>(false);
  const [modalRecipesForPhysicalStoreVisible, setModalRecipesForPhysicalStoreVisible] = React.useState<boolean>(false);
  const [modalRecipesReadOnly, setModalRecipesReadOnly] = React.useState<boolean>(false);
  const [modalRecipesTitle, setModalRecipesTitle] = React.useState<string>(strings.Recipes);
  const [modalRecipesOrderId, setModalRecipesOrderId] = React.useState<number | undefined>(undefined);
  const [modalRecipesOrderNumber, setModalRecipesOrderNumber] = React.useState<string | undefined>(undefined);
  const [modalRecipesOrderDataSource, setModalRecipesOrderDataSource] = React.useState<DataSource | undefined>(undefined);
  const [modalNotificationsVisible, setModalNotificationsVisible] = React.useState<boolean>(false);
  const [modalNotificationsTitle, setModalNotificationsTitle] = React.useState<string>(strings.Notifications);
  const [modalNotificationsOrderId, setModalNotificationsOrderId] = React.useState<number | undefined>(undefined);
  const [modalNotificationsOrderNumber, setModalNotificationsOrderNumber] = React.useState<string | undefined>(undefined);
  const [modalNotificationsOrderEmail, setModalNotificationsOrderEmail] = React.useState<string | undefined>(undefined);
  const [modalNotificationsOrderPhone, setModalNotificationsOrderPhone] = React.useState<string | undefined>(undefined);
  const [modalDisapproveVisible, setModalDisapproveVisible] = React.useState<boolean>(false);
  const [modalDisapproveOrderId, setModalDisapproveOrderId] = React.useState<number | undefined>(undefined);

  const { toastService } = React.useContext(ServicesContext);
  const {
    saveNewStatePrescription,
    getButtonPrescriptionOptions,
    registerAssemblyDisapproval,
    stopChangeIfOrderTagsIncludesExternalChange
  } = useOrderUtils();

  const currentStatePrescriptionId = currentStateId || StatePrescriptionIdentification.SemReceita;

  return (
    <>
      <DropdownButton
        items={getButtonPrescriptionOptions(currentStatePrescriptionId, orderIsPhysicalStore, 
          customerShopifyId, orderIsCrossChannelSale, 
          orderLocationId, orderOriginLocationId)}
        defaultStyle={true}
        small={true}
        buttonColor={DropdownButtonColor.Info}
        text={''}
        clickItem={(key) => handlePrescriptionOption(key)}
        marginLeft={'3px'}
      />
      <ModalRecipes
        visible={modalRecipesVisible}
        readonly={modalRecipesReadOnly}
        close={(orderId) => closeModalRecipes(orderId)}
        title={modalRecipesTitle}
        orderId={modalRecipesOrderId}
        orderNumber={modalRecipesOrderNumber}
        orderDataSource={modalRecipesOrderDataSource}
        orderIsPhysicalStore={orderIsPhysicalStore || false}
        customerId={customerId}
      />
      <ModalRecipesV2
        visible={modalRecipesV2Visible}
        readonly={modalRecipesReadOnly}
        close={(orderId) => closeModalRecipes(orderId)}
        title={modalRecipesTitle}
        orderId={modalRecipesOrderId}
        orderNumber={modalRecipesOrderNumber}
        orderDataSource={modalRecipesOrderDataSource}
        customerShopifyId={customerShopifyId}
        customerId={customerId}
      />
      <ModalRecipesForPhysicalStore
        visible={modalRecipesForPhysicalStoreVisible}
        close={(orderId) => closeModalRecipes(orderId)}
        title={modalRecipesTitle}
        orderId={modalRecipesOrderId}
        orderNumber={modalRecipesOrderNumber}
        customerId={customerId}
        customerShopifyId={customerShopifyId}
        isCrossChannelSale={orderIsCrossChannelSale}
        currentStatePrescriptionId={currentStatePrescriptionId}
      />
      <ModalNotificationsV2
        visible={modalNotificationsVisible}
        close={(orderId) => closeModalNotifications(orderId)}
        title={modalNotificationsTitle}
        orderId={modalNotificationsOrderId}
        orderNumber={modalNotificationsOrderNumber}
        orderEmail={modalNotificationsOrderEmail}
        orderPhone={modalNotificationsOrderPhone}
      />
      {modalDisapproveVisible ?
      <ModalDisapproveDegree
        visible={modalDisapproveVisible}
        orderId={modalDisapproveOrderId}
        close={() => closeModalDisapprove()}
        registerAssemblyDisapproval={(orderId, problemType, problemDetails) => handleAssemblyDisapproval(orderId, problemType, problemDetails)}
      /> : null}
    </>
  );

  function handlePrescriptionOption(key: any) {
    if (!orderId) {
      toastService.warning(strings.MessageToPrintingSelectedOrdersEmpty);
      return;
    }

    if (key === PrescriptionOption.Recipes) {
      setModalRecipesVisible(true);
      setModalRecipesReadOnly(false);
      setModalRecipesTitle(`${strings.Recipes}`);
      setModalRecipesOrderId(orderId);
      setModalRecipesOrderNumber(orderNumber);
      setModalRecipesOrderDataSource(orderDataSource);
      if (modalOpened) modalOpened();
      return;
    }

    if (key === PrescriptionOption.RecipesV2) {
      setModalRecipesV2Visible(true);
      setModalRecipesReadOnly(false);
      setModalRecipesTitle(`${strings.Recipes}`);
      setModalRecipesOrderId(orderId);
      setModalRecipesOrderNumber(orderNumber);
      setModalRecipesOrderDataSource(orderDataSource);
      if (modalOpened) modalOpened();
      return;
    }

    if (key === PrescriptionOption.RecipesForPhysicalStore) {
        setModalRecipesForPhysicalStoreVisible(true);
        setModalRecipesReadOnly(false);
        setModalRecipesTitle(`${strings.Recipes}`);
        setModalRecipesOrderId(orderId);
        setModalRecipesOrderNumber(orderNumber);
        setModalRecipesOrderDataSource(orderDataSource);
        if (modalOpened) modalOpened();
        return;
      }

    if (key === PrescriptionOption.NotificationsV2) {
      setModalNotificationsVisible(true);
      setModalNotificationsTitle(`${strings.Notifications}`);
      setModalNotificationsOrderId(orderId);
      setModalNotificationsOrderNumber(orderNumber);
      setModalNotificationsOrderEmail(customerEmail);
      setModalNotificationsOrderPhone(customerPhone);
      if (modalOpened) modalOpened();
      return;
    }

    if (stopChangeIfOrderTagsIncludesExternalChange(orderTags)) {
      return;
    }

    if (key === PrescriptionOption.RequestLens) {
      saveNewStatePrescription(StatePrescriptionIdentification.AguardandoLente, orderId)
        .then(() => statePrescriptionChanged && statePrescriptionChanged(StatePrescriptionIdentification.AguardandoLente));
      return;
    }

    if (key === PrescriptionOption.RequestInternalManufacturing) {
      saveNewStatePrescription(StatePrescriptionIdentification.AguardandoMontagemInterna, orderId)
        .then(() => statePrescriptionChanged && statePrescriptionChanged(StatePrescriptionIdentification.AguardandoMontagemInterna));
      return;
    }

    if (key === PrescriptionOption.RequestExternalManufacturing) {
      saveNewStatePrescription(StatePrescriptionIdentification.AguardandoMontagemExterna, orderId)
        .then(() => statePrescriptionChanged && statePrescriptionChanged(StatePrescriptionIdentification.AguardandoMontagemExterna));
      return;
    }

    if (key === PrescriptionOption.StartChecking) {
      saveNewStatePrescription(StatePrescriptionIdentification.AguardandoConferencia, orderId)
        .then(() => statePrescriptionChanged && statePrescriptionChanged(StatePrescriptionIdentification.AguardandoConferencia));
      return;
    }

    if (key === PrescriptionOption.Approve) {
      saveNewStatePrescription(StatePrescriptionIdentification.Ok, orderId)
        .then(() => statePrescriptionChanged && statePrescriptionChanged(StatePrescriptionIdentification.Ok));
      return;
    }

    if (key === PrescriptionOption.Disapprove) {
      setModalDisapproveOrderId(orderId);
      setModalDisapproveVisible(true);
      if (modalOpened) modalOpened();
      return;
    }
  }

  function closeModalRecipes(orderId?: number) {
    setModalRecipesVisible(false);
    setModalRecipesV2Visible(false);
    setModalRecipesForPhysicalStoreVisible(false);
    setModalRecipesReadOnly(false);
    if (modalClosed) modalClosed(true);
  }

  function closeModalNotifications(orderId?: number) {
    setModalNotificationsVisible(false);
    if (modalClosed) modalClosed();
  }

  function closeModalDisapprove() {
    setModalDisapproveOrderId(undefined);
    setModalDisapproveVisible(false);
    if (modalClosed) modalClosed(true);
  }

  function handleAssemblyDisapproval(orderId: number, problemType: AssemblyDisapprovalType, problemDetails: string) {
    closeModalDisapprove();
    registerAssemblyDisapproval(orderId, problemType, problemDetails, () => statePrescriptionChanged && statePrescriptionChanged());
  }
}